import { useEffect, useState } from "react";
import { X, Check, Plus, ToggleLeft, ToggleRight, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal de gestión de categorías de inventario (iter 71).
 * Iter 80 — Generalizado para reusar con orígenes de captación (Customer
 * Referral Sources) sin duplicar 200 líneas. Props opcionales:
 *
 *  - endpoint: default "/settings/inventory-categories"
 *  - title:    default "Gestionar categorías"
 *  - itemLabel: default "categorías" (texto de "Categorías guardadas", "Nueva categoría…")
 *  - testidPrefix: default "inventory-cat"  (sin trailing dash)
 *  - modalTestid: default "inventory-cats-modal"
 *  - saveBtnTestid: default "inventory-cats-save"
 *  - itemNewLabelTestid: default "inventory-cat-new-label"
 *  - itemAddTestid: default "inventory-cat-add"
 *  - itemCloseTestid: default "inventory-cats-close"
 *
 * Reglas:
 *  - NO permite eliminar (solo desactivar) para preservar integridad histórica.
 *  - Bloquea duplicados case-insensitive.
 *  - Normaliza espacios.
 *  - Reordenable.
 *
 * Props:
 *  - onClose
 *  - onSaved(newCategories)
 *  - isAdmin: si false, controles deshabilitados.
 */
export default function InventoryCategoryManager({
  onClose, onSaved, isAdmin,
  endpoint = "/settings/inventory-categories",
  title = "Gestionar categorías",
  itemLabelPlural = "categorías",
  itemLabelSingular = "categoría",
  testidPrefix = "inventory-cat",
  modalTestid = "inventory-cats-modal",
  saveBtnTestid = "inventory-cats-save",
  newLabelTestid = "inventory-cat-new-label",
  addTestid = "inventory-cat-add",
  closeTestid = "inventory-cats-close",
  iterTag = "// Iter 71",
}) {
  const [draft, setDraft] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(endpoint)
      .then((r) => setDraft((r.data || []).map((c) => ({ ...c }))))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const norm = (s) => (s || "").split(/\s+/).filter(Boolean).join(" ");
  const slug = (s) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase().replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "").slice(0, 40) || `cat_${Date.now()}`;

  const addCat = () => {
    const label = norm(newLabel);
    if (!label) return;
    const exists = draft.some((c) => (c.label || "").toLowerCase() === label.toLowerCase());
    if (exists) {
      toast.error("Ya existe una categoría con ese nombre");
      return;
    }
    let id = slug(label);
    if (draft.some((c) => c.id === id)) id = `${id}_${draft.length}`;
    setDraft([...draft, { id, label, active: true, order: draft.length }]);
    setNewLabel("");
  };

  const editLabel = (idx, val) => {
    const lst = draft.slice();
    lst[idx] = { ...lst[idx], label: val };
    setDraft(lst);
  };

  const toggleActive = (idx) => {
    const lst = draft.slice();
    lst[idx] = { ...lst[idx], active: !lst[idx].active };
    setDraft(lst);
  };

  const move = (idx, dir) => {
    const lst = draft.slice();
    const target = idx + dir;
    if (target < 0 || target >= lst.length) return;
    [lst[idx], lst[target]] = [lst[target], lst[idx]];
    setDraft(lst);
  };

  const save = async () => {
    // pre-check de duplicados case-insensitive en cliente
    const labels = draft.map((c) => norm(c.label).toLowerCase());
    const dup = labels.find((v, i) => v && labels.indexOf(v) !== i);
    if (dup) {
      toast.error(`${itemLabelSingular[0].toUpperCase() + itemLabelSingular.slice(1)} duplicada: "${dup}"`);
      return;
    }
    setSaving(true);
    try {
      const payload = draft.map((c, i) => ({
        id: c.id || slug(norm(c.label)),
        label: norm(c.label),
        active: !!c.active,
        order: i,
      }));
      const r = await api.put(endpoint, payload);
      toast.success(`${itemLabelPlural[0].toUpperCase() + itemLabelPlural.slice(1)} guardad${itemLabelPlural.endsWith("s") ? "as" : "a"}`);
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={modalTestid}
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{iterTag}</div>
            <div className="font-display font-bold text-base">{title}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]" data-testid={closeTestid}>
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && <div className="text-sm text-[#525252] text-center py-6">Cargando...</div>}
          {!loading && draft.length === 0 && (
            <div className="text-sm text-[#525252] text-center py-6">
              Aún no hay {itemLabelPlural}. Añade la primera abajo.
            </div>
          )}
          {draft.map((cat, i) => (
            <div
              key={cat.id || i}
              data-testid={`${testidPrefix}-${cat.id}`}
              className={`border ${cat.active ? "border-[#E5E5E0] bg-white" : "border-[#E5E5E0] bg-[#F7F7F4] opacity-70"} px-3 py-2 flex items-center gap-2`}
            >
              <input
                value={cat.label}
                onChange={(e) => editLabel(i, e.target.value)}
                disabled={!isAdmin}
                data-testid={`${testidPrefix}-label-${cat.id}`}
                className="flex-1 bg-transparent border-b border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-1 py-1 text-sm"
              />
              <span className="font-mono text-[9px] text-[#A8A8A0]">{cat.id}</span>
              <button onClick={() => move(i, -1)} disabled={!isAdmin || i === 0} title="Subir"
                      data-testid={`${testidPrefix}-up-${cat.id}`}
                      className="p-1 border border-[#E5E5E0] hover:border-[#111110] disabled:opacity-30">
                <ArrowUp size={12} />
              </button>
              <button onClick={() => move(i, +1)} disabled={!isAdmin || i === draft.length - 1} title="Bajar"
                      data-testid={`${testidPrefix}-down-${cat.id}`}
                      className="p-1 border border-[#E5E5E0] hover:border-[#111110] disabled:opacity-30">
                <ArrowDown size={12} />
              </button>
              <button onClick={() => toggleActive(i)} disabled={!isAdmin}
                      title={cat.active ? "Desactivar" : "Activar"}
                      data-testid={`${testidPrefix}-toggle-${cat.id}`}
                      className="p-1 border border-[#E5E5E0] hover:border-[#111110] disabled:opacity-30">
                {cat.active ? <ToggleRight size={14} className="text-emerald-600" /> : <ToggleLeft size={14} className="text-[#A8A8A0]" />}
              </button>
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="border-t border-[#E5E5E0] p-3 bg-[#F7F7F4] flex items-center gap-2">
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCat())}
              placeholder={`Nueva ${itemLabelSingular}...`}
              data-testid={newLabelTestid}
              className="flex-1 ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm"
            />
            <button onClick={addCat} data-testid={addTestid}
                    className="inline-flex items-center gap-1 bg-[#111110] hover:bg-black text-white px-3 py-2 text-xs font-semibold">
              <Plus size={12} /> Añadir
            </button>
          </div>
        )}

        <div className="border-t border-[#E5E5E0] p-3 flex justify-end gap-2">
          <button onClick={onClose}
                  className="border border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-xs">
            Cancelar
          </button>
          <button onClick={save} disabled={saving || !isAdmin}
                  data-testid={saveBtnTestid}
                  className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-xs font-semibold disabled:opacity-50">
            <Check size={12} /> {saving ? "Guardando..." : `Guardar ${itemLabelPlural}`}
          </button>
        </div>
      </div>
    </div>
  );
}
